import { GetSellerPayLinkResponse } from '@iwoca/lapi-client/iwocapay';
import { Button, Table } from '@iwoca/orion';
import { ColumnDef } from '@iwoca/orion/lib/components/table/Table';

import { buildQueryString } from '../../../../../Buyer/utils/queryParams';
import { displayCurrency } from '../../../../../utils/DisplayCurrency';
import { productDurationToReadable } from '../../../../../utils/ProductDuration';
import { formatDate } from '../../../../Fees/RateContextHeader/RateContextHeader';
import { usePayLinks } from '../../../../hooks/usePaylinks.hook';
import { TSelectedBuyer } from '../../../Customers.types';

type LatestPaymentsTableColumns = {
  date: string;
  companyName: string;
  reference: string;
  amount: number;
  duration: string;
};

const normaliseTableData = (
  payLinks: GetSellerPayLinkResponse['data'],
): LatestPaymentsTableColumns[] => {
  return payLinks.map((payLink) => ({
    date: payLink.created || '',
    companyName: payLink.buyer_name,
    reference: payLink.reference,
    amount: payLink.amount,
    duration: productDurationToReadable(payLink.product_duration) || '',
  }));
};

export const LatestPaymentsTable = ({
  selectedBuyer,
}: {
  selectedBuyer: TSelectedBuyer;
}) => {
  const { payLinks, isLoading: loadingPayLinks } = usePayLinks({
    buyerStateKey: selectedBuyer.stateKey,
    onlyFunded: true,
    includeArchivedAndUnarchived: true,
  });
  const queryString = buildQueryString({
    search: selectedBuyer.name,
  });
  const latestPaylinks = payLinks.slice(0, 3);
  const tableData = normaliseTableData(latestPaylinks);
  const columns: ColumnDef<LatestPaymentsTableColumns>[] = [
    {
      cell: (info) => (
        <div className="flex h-full items-center text-s">
          {formatDate(info.row.original.date, 'short')}
        </div>
      ),
      id: 'date',
      header: 'Date',
      className: '!text-s font-med text-primary-10 w-1/4',
    },
    {
      cell: (info) => (
        <div className="block">
          <div className="text-s font-med">{info.row.original.reference}</div>
          <div className="text-xs font-reg">
            {info.row.original.companyName}
          </div>
        </div>
      ),
      header: 'Ref & Client',
      className: '!text-s font-med text-primary-10 w-1/2 xs-480:w-[45%]',
    },
    {
      cell: (info) => (
        <div className="block">
          <div className="text-s font-med">
            {displayCurrency(info.row.original.amount)}
          </div>
          <div className="text-xs font-reg text-structure-40">
            {info.row.original.duration}
          </div>
        </div>
      ),
      header: 'Amount',
      className: '!text-s font-med text-primary-10 w-1/4 xs-480:w-[30%]',
    },
  ];

  return (
    <div>
      <div className="mb-l mt-s text-l font-med">Latest payments</div>
      <Table
        data={tableData}
        columns={columns}
        loading={loadingPayLinks}
        className="[&_td]:px-l [&_td]:py-r [&_thead_th:nth-child(1)]:w-1/4 [&_thead_th:nth-child(2)]:w-1/2 xs-480:[&_thead_th:nth-child(2)]:w-[45%] [&_thead_th:nth-child(3)]:w-1/4 xs-480:[&_thead_th:nth-child(3)]:w-[30%] [&_tr]:w-full"
      />
      <div className="mt-l flex justify-center">
        <Button
          href={`/pay/payments/${queryString}`}
          className="!px-2xl"
          variant="secondary"
        >
          See older payments
        </Button>
      </div>
    </div>
  );
};
